import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { APP_NAME } from '../config'
import { Verbindung, type TvInhalt, type Verbindungslage } from '../kopplung/verbindung'
import { Maskottchen } from '../spiel/Maskottchen'
import { Sterne, Konfetti } from '../spiel/Deko'

const CODE_SCHLUESSEL = 'zahlenflug:tv-code'

/**
 * Die Ansicht auf dem Fernseher.
 *
 * Haelt bewusst keinen eigenen Zustand ueber das Spiel: Sie zeigt genau das,
 * was zuletzt vom Tablet kam. Nach einem Neuladen fragt sie einmal nach und
 * ist sofort wieder aktuell. Zwei Geraete, die beide mitrechnen, waeren zwei
 * Wahrheiten, die frueher oder spaeter auseinanderlaufen.
 */
export function TvAnsicht() {
  const [lage, setLage] = useState<Verbindungslage>('verbinde')
  const [code, setCode] = useState<string | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [inhalt, setInhalt] = useState<TvInhalt>({ art: 'warten' })
  const [grund, setGrund] = useState<string | null>(null)
  const verbindung = useRef<Verbindung | null>(null)

  useEffect(() => {
    const v = new Verbindung({
      onLage: (neu, warum) => {
        setLage(neu)
        setGrund(warum ?? null)
        if (neu === 'gekoppelt') v.standAnfordern()
      },
      onCode: (neuerCode) => {
        setCode(neuerCode)
        // Im sessionStorage, nicht im localStorage: Der Code soll nur ein
        // Neuladen ueberleben, nicht das Ausschalten des Fernsehers.
        try {
          window.sessionStorage.setItem(CODE_SCHLUESSEL, neuerCode)
        } catch {
          /* Ohne Sitzungsspeicher gibt es eben nach dem Neuladen einen neuen Code. */
        }
        const ziel = `${window.location.origin}/koppeln?code=${neuerCode}`
        void QRCode.toDataURL(ziel, { margin: 1, width: 420, color: { dark: '#1a1147', light: '#fff8e7' } })
          .then(setQr)
          .catch(() => setQr(null))
      },
      onZeigen: setInhalt,
    })
    verbindung.current = v
    let vorheriger: string | null = null
    try {
      vorheriger = window.sessionStorage.getItem(CODE_SCHLUESSEL)
    } catch {
      vorheriger = null
    }
    v.alsTv(vorheriger)
    return () => v.schliessen()
  }, [])

  if (lage === 'fehler' || lage === 'getrennt') {
    return (
      <Rahmen>
        <Maskottchen stimmung="trost" gross />
        <h1 className="titel">Keine Verbindung</h1>
        <p className="tv-hinweis">
          {grund === 'tv-weg'
            ? 'Die Verbindung wurde getrennt.'
            : 'Der Fernseher erreicht den Zahlenflug-Server nicht.'}{' '}
          Lade die Seite neu. Auf dem Tablet allein funktioniert das Üben weiterhin.
        </p>
      </Rahmen>
    )
  }

  if (lage !== 'gekoppelt') {
    return (
      <Rahmen>
        <h1 className="titel">{APP_NAME}</h1>
        <p className="tv-hinweis">Tippe diesen Code auf dem Tablet ein oder scanne das Bild:</p>
        <div className="tv-code">{code ?? '····'}</div>
        {qr && (
          <div className="qr">
            <img src={qr} alt={`QR-Code zum Verbinden, Code ${code}`} />
          </div>
        )}
      </Rahmen>
    )
  }

  return <Gekoppelt inhalt={inhalt} />
}

function Gekoppelt({ inhalt }: { inhalt: TvInhalt }) {
  if (inhalt.art === 'warten') {
    return (
      <Rahmen>
        <Maskottchen stimmung="begeistert" gross />
        <h1 className="titel">Bereit!</h1>
        <p className="tv-hinweis">Tippe auf dem Tablet auf „Losgehen“.</p>
      </Rahmen>
    )
  }

  if (inhalt.art === 'aufgabe') {
    return (
      <Rahmen>
        <span className="zaehler" style={{ marginBottom: '2vmin' }}>
          {inhalt.nummer} / {inhalt.gesamt}
        </span>
        <h2 className="aufgabe aufgabe--tv">
          {inhalt.text} <span className="aufgabe__frage">= ?</span>
        </h2>
        {inhalt.antworten && (
          <div className="tv-ballons">
            {inhalt.antworten.map((wert, i) => (
              <div key={`${wert}-${i}`} className="tv-ballon">
                {wert}
              </div>
            ))}
          </div>
        )}
      </Rahmen>
    )
  }

  if (inhalt.art === 'rueckmeldung') {
    return (
      <Rahmen>
        <Maskottchen stimmung={inhalt.richtig ? 'begeistert' : 'trost'} gross />
        <h2 className="aufgabe aufgabe--tv">
          {inhalt.text} = {inhalt.loesung}
        </h2>
        {inhalt.merksatz && <p className="rueck__merksatz">{inhalt.merksatz}</p>}
      </Rahmen>
    )
  }

  if (inhalt.art === 'kette') {
    return (
      <Rahmen>
        <Konfetti anzahl={30} />
        <Maskottchen stimmung="feiert" gross />
        <h1 className="titel">{inhalt.laenge} richtig in Folge!</h1>
      </Rahmen>
    )
  }

  const sekunden = inhalt.schnellsteMs ? (inhalt.schnellsteMs / 1000).toFixed(1) : null
  return (
    <Rahmen>
      <Konfetti />
      <Maskottchen stimmung="feiert" gross />
      <h1 className="titel">Geschafft!</h1>
      <div className="karten">
        <div className="karte">
          <div className="karte__wert">
            {inhalt.gewusst} / {inhalt.gesamt}
          </div>
          <div className="karte__name">sofort gewusst</div>
        </div>
        {sekunden && (
          <div className="karte">
            <div className="karte__wert">{sekunden}s</div>
            <div className="karte__name">schnellste Antwort</div>
          </div>
        )}
      </div>
    </Rahmen>
  )
}

function Rahmen({ children }: { children: React.ReactNode }) {
  return (
    <div className="buehne">
      <Sterne anzahl={55} />
      <div className="mitte">{children}</div>
    </div>
  )
}
